/**
 * ACS Lookup credits — client-side helpers.
 *
 * Buy flow:
 *   1. Connect wallet, switch to Base mainnet (chain 8453)
 *   2. USDC.transfer(TREASURY, numCredits * 500_000)   — 0.5 USDC per credit
 *   3. POST tx hash to /api/acs/purchase-credits — backend verifies and mints credits
 *
 * Lookup flow:
 *   1. POST /api/acs/lookup with wallet + agent_id
 *   2. Backend decrements 1 credit and returns the ACS score payload
 */
import { ethers } from 'ethers';

const BASE_CHAIN_ID = 8453;
const USDC_ADDRESS = process.env.REACT_APP_USDC_BASE_ADDRESS
  || '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913';
const TREASURY_ADDRESS = '0x0b81b7Db079Ac596b08a72FBBf87FF2D4655ED37';
const PRICE_PER_LOOKUP_UUSD = 500000n; // 0.5 USDC (6 decimals)
const API = process.env.REACT_APP_BACKEND_URL;

const USDC_ABI = [
  'function transfer(address to, uint256 amount) returns (bool)',
  'function balanceOf(address who) view returns (uint256)',
];

async function ensureBaseMainnet(provider) {
  const network = await provider.getNetwork();
  if (Number(network.chainId) !== BASE_CHAIN_ID) {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x2105' }],
      });
    } catch {
      throw new Error('Please switch your wallet to Base mainnet.');
    }
  }
}

/** Connect a wallet and return the connected address (lowercased). */
export async function connectWallet() {
  if (!window.ethereum) {
    throw new Error('No wallet detected. Install MetaMask, Rabby, or Coinbase Wallet.');
  }
  const provider = new ethers.BrowserProvider(window.ethereum);
  await provider.send('eth_requestAccounts', []);
  await ensureBaseMainnet(provider);
  const signer = await provider.getSigner();
  return (await signer.getAddress()).toLowerCase();
}

/** Read the credit balance for a wallet. */
export async function getBalance(wallet) {
  const r = await fetch(`${API}/api/acs/balance/${wallet.toLowerCase()}`);
  if (!r.ok) throw new Error(`Balance lookup failed: ${r.status}`);
  const d = await r.json();
  return d.balance ?? 0;
}

/** Buy `numCredits` lookups. Runs the USDC.transfer + backend credit request. */
export async function buyCredits(numCredits, onStep = () => {}) {
  if (!Number.isInteger(numCredits) || numCredits < 1) {
    throw new Error('numCredits must be a positive integer.');
  }

  onStep('connecting');
  const provider = new ethers.BrowserProvider(window.ethereum);
  await provider.send('eth_requestAccounts', []);
  await ensureBaseMainnet(provider);
  const signer = await provider.getSigner();
  const buyer = (await signer.getAddress()).toLowerCase();

  const amount = PRICE_PER_LOOKUP_UUSD * BigInt(numCredits);
  const usdc = new ethers.Contract(USDC_ADDRESS, USDC_ABI, signer);

  const balance = await usdc.balanceOf(buyer);
  if (balance < amount) {
    const need = ethers.formatUnits(amount, 6);
    const have = ethers.formatUnits(balance, 6);
    throw new Error(`Insufficient USDC. Need ${need}, have ${have}. Bridge USDC to Base first.`);
  }

  onStep('confirming-in-wallet');
  const tx = await usdc.transfer(TREASURY_ADDRESS, amount);
  onStep('waiting-for-confirmation');
  const receipt = await tx.wait();

  onStep('crediting');
  const r = await fetch(`${API}/api/acs/purchase-credits`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tx_hash: receipt.hash, wallet: buyer }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.detail || `Backend rejected credit request (HTTP ${r.status}).`);
  }
  const payload = await r.json();

  onStep('done');
  return {
    ...payload,
    buyer,
    explorer: `https://basescan.org/tx/${receipt.hash}`,
  };
}

/** Consume 1 credit and fetch an ACS score. Returns the full payload. */
export async function lookupACS(wallet, agentId) {
  const r = await fetch(`${API}/api/acs/lookup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ wallet: wallet.toLowerCase(), agent_id: agentId }),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.detail || `Lookup failed (HTTP ${r.status}).`);
  return data;
}
